/**
 * AMPD DCMS2 — file field (Input with InputTypes::File, template input.file.tpl)
 *
 * The native <input type="file"> stays in the form with its name, id and
 * attributes (multipart submit, $_FILES, `required`/Parsley and
 * setValidationState work unchanged) but is visually hidden. This module:
 *  - opens the native dialog from the visible picker,
 *  - lists the chosen files (name, size) with a remove button for each one,
 *  - single file: the picker hides while a file is chosen; removing the file
 *    brings it back (remove = "change file"),
 *  - `multiple`: a new selection (dialog or drop) is ADDED to the list
 *    instead of replacing it (native behaviour); duplicates are skipped and
 *    the picker stays below the list as "Add file",
 *  - accepts files dropped onto the field,
 *  - keeps input.files in sync (DataTransfer) and emits `input` + `change`
 *    after a removal or a drop, so validators and listeners see the real
 *    selection.
 *
 * Event delegation on document: fields added later (formBuilder, AJAX) work
 * without re-initialisation; no cost on pages without the field. The
 * selection is merged in the capture phase, so every `input`/`change`
 * listener of the field (also inline onchange) already sees the full list.
 *
 * Public API (window.DCMSFileInput):
 *   refresh(target) — re-render the list after a programmatic change of
 *                     input.files / input.value (target: input or any
 *                     element inside the field)
 *   clear(target)   — remove every file (emits input + change)
 *   version         — API level for feature detection
 */
(function () {
    'use strict';

    if (window.DCMSFileInput) { return; }   // double-include guard (bundle + src)

    var ROOT = '[data-dcms-file-input]';

    /** Last known selection per input — restores the list when a browser clears it on dialog cancel. */
    var selections = new WeakMap();

    /** Set while this module dispatches its own input/change events. */
    var emitting = false;

    function rootOf(node) {
        return node && node.closest ? node.closest(ROOT) : null;
    }

    function inputOf(root) {
        return root ? root.querySelector('input[type="file"]') : null;
    }

    function resolve(target) {
        var root = rootOf(target);
        if (!root && target && target.querySelector) {
            root = target.matches && target.matches(ROOT) ? target : target.querySelector(ROOT);
        }
        return root;
    }

    function isLocked(input) {
        return input.disabled || input.hasAttribute('readonly');
    }

    function fileKey(file) {
        return file.name + '\u0000' + file.size + '\u0000' + file.lastModified;
    }

    function sameSelection(a, b) {
        if (a.length !== b.length) { return false; }
        for (var i = 0; i < a.length; i++) {
            if (fileKey(a[i]) !== fileKey(b[i])) { return false; }
        }
        return true;
    }

    function mergeFiles(previous, incoming) {
        var seen = new Set(previous.map(fileKey));
        return previous.concat(incoming.filter(function (file) {
            var key = fileKey(file);
            if (seen.has(key)) { return false; }
            seen.add(key);
            return true;
        }));
    }

    /**
     * Writes the selection into input.files. Returns false when the browser
     * cannot build a FileList (no DataTransfer constructor) — then only a full
     * clear is possible.
     */
    function writeFiles(input, files) {
        try {
            var transfer = new DataTransfer();
            files.forEach(function (file) { transfer.items.add(file); });
            input.files = transfer.files;
        } catch (e) {
            if (files.length > 0) { return false; }
            input.value = '';
        }
        selections.set(input, files.slice());
        return true;
    }

    function emit(input) {
        emitting = true;
        try {
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
            emitting = false;
        }
    }

    function formatSize(bytes) {
        var units = ['B', 'KB', 'MB', 'GB'];
        var value = bytes;
        var unit = 0;
        while (value >= 1024 && unit < units.length - 1) {
            value /= 1024;
            unit++;
        }
        // DCMS UI language first — <html lang> does not always follow it.
        var lang = (typeof DCMS_CURRENT_LANGUAGE === 'string' && DCMS_CURRENT_LANGUAGE)
            || document.documentElement.lang || undefined;
        var number;
        try {
            number = new Intl.NumberFormat(lang, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value);
        } catch (e) {
            number = String(Math.round(value * 10) / 10);
        }
        return number + ' ' + units[unit];
    }

    function render(root) {
        var input = inputOf(root);
        var list = root.querySelector('[data-dcms-file-list]');
        var template = root.querySelector('template[data-dcms-file-item]');
        if (!input || !list || !template) { return; }

        var files = Array.from(input.files || []);
        var locked = isLocked(input);
        var texts = root.dataset;
        selections.set(input, files);

        list.replaceChildren();
        files.forEach(function (file, index) {
            var item = template.content.firstElementChild.cloneNode(true);
            var name = item.querySelector('[data-dcms-file-name]');
            var size = item.querySelector('[data-dcms-file-size]');
            var icon = item.querySelector('[data-dcms-file-icon]');
            var remove = item.querySelector('[data-dcms-file-remove]');
            if (name) {
                name.textContent = file.name;
                name.title = file.name;
            }
            if (size) { size.textContent = formatSize(file.size); }
            if (icon && file.type && file.type.indexOf('image/') === 0) { icon.textContent = 'image'; }
            if (remove) {
                var label = (texts.textRemove || remove.getAttribute('aria-label') || '') + ': ' + file.name;
                remove.dataset.dcmsFileIndex = String(index);
                remove.setAttribute('aria-label', label);
                remove.title = label;
                remove.hidden = locked;
            }
            list.appendChild(item);
        });
        list.hidden = files.length === 0;

        var picker = root.querySelector('[data-dcms-file-picker]');
        if (picker) { picker.hidden = !input.multiple && files.length > 0; }
        var pickerText = root.querySelector('[data-dcms-file-picker-text]');
        if (pickerText) {
            var text = input.multiple
                ? (files.length > 0 ? texts.textAdd : texts.textChooseMultiple)
                : texts.textChoose;
            if (text) { pickerText.textContent = text; }
        }
        root.toggleAttribute('data-has-files', files.length > 0);
    }

    function removeAt(root, index) {
        var input = inputOf(root);
        if (!input || isLocked(input)) { return; }
        var files = Array.from(input.files || []);
        files.splice(index, 1);
        if (!writeFiles(input, files)) {
            writeFiles(input, []);   // no DataTransfer: only a full clear is possible
        }
        render(root);
        emit(input);

        // Keep keyboard focus inside the field: next remove button, else the input (focus ring on the picker).
        var buttons = root.querySelectorAll('[data-dcms-file-remove]');
        var next = buttons[Math.min(index, buttons.length - 1)];
        (next && !next.hidden ? next : input).focus({ preventScroll: true });
    }

    /** Native input/change of the field: merge (multiple) or restore after a cancelled dialog. */
    function onSelection(event) {
        var input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'file') { return; }
        var root = rootOf(input);
        if (!root) { return; }
        if (emitting) {
            render(root);
            return;
        }
        var previous = selections.get(input) || [];
        var chosen = Array.from(input.files || []);
        if (sameSelection(chosen, previous)) {
            return;   // already handled (input fires before change)
        }
        if (chosen.length === 0 && previous.length > 0) {
            // Dialog cancelled — some browsers clear the selection then; keep the list.
            writeFiles(input, previous);
        } else if (input.multiple && previous.length > 0) {
            writeFiles(input, mergeFiles(previous, chosen));
        }
        render(root);
    }

    function hasFiles(dataTransfer) {
        return !!dataTransfer && Array.from(dataTransfer.types || []).indexOf('Files') !== -1;
    }

    function droppableRoot(event) {
        var root = rootOf(event.target);
        var input = inputOf(root);
        if (!input || isLocked(input) || !hasFiles(event.dataTransfer)) { return null; }
        return root;
    }

    document.addEventListener('input', onSelection, true);
    document.addEventListener('change', onSelection, true);

    document.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) { return; }

        // `readonly` does not apply to file inputs natively — a label click or Space/Enter
        // (both end as a click on the input) would still open the dialog.
        if (target instanceof HTMLInputElement && target.type === 'file'
            && target.hasAttribute('readonly') && rootOf(target)) {
            event.preventDefault();
            return;
        }

        var remove = target.closest('[data-dcms-file-remove]');
        if (remove) {
            var removeRoot = rootOf(remove);
            if (removeRoot) {
                event.preventDefault();
                removeAt(removeRoot, parseInt(remove.dataset.dcmsFileIndex, 10) || 0);
            }
            return;
        }

        var picker = target.closest('[data-dcms-file-picker]');
        if (picker) {
            var input = inputOf(rootOf(picker));
            if (input && !isLocked(input)) { input.click(); }
        }
    });

    document.addEventListener('dragover', function (event) {
        var root = droppableRoot(event);
        if (!root) { return; }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        root.setAttribute('data-dragover', '');
    });

    document.addEventListener('dragleave', function (event) {
        var root = rootOf(event.target);
        if (!root || (event.relatedTarget && root.contains(event.relatedTarget))) { return; }
        root.removeAttribute('data-dragover');
    });

    document.addEventListener('drop', function (event) {
        var root = rootOf(event.target);
        if (root) { root.removeAttribute('data-dragover'); }
        root = droppableRoot(event);
        if (!root) { return; }
        event.preventDefault();
        var input = inputOf(root);
        var dropped = Array.from(event.dataTransfer.files || []);
        if (dropped.length === 0) { return; }
        var next = input.multiple
            ? mergeFiles(selections.get(input) || Array.from(input.files || []), dropped)
            : dropped.slice(0, 1);
        if (!writeFiles(input, next)) { return; }
        render(root);
        emit(input);
    });

    // form.reset() clears file inputs without a change event.
    document.addEventListener('reset', function (event) {
        var form = event.target;
        if (!form || !form.querySelectorAll) { return; }
        setTimeout(function () {
            form.querySelectorAll(ROOT).forEach(render);
        }, 0);
    }, true);

    function renderExisting() {
        document.querySelectorAll(ROOT).forEach(function (root) {
            var input = inputOf(root);
            // Restored selection (back/forward cache) or `multiple` set by a raw attribute.
            if (input && (input.multiple || (input.files && input.files.length > 0))) { render(root); }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', renderExisting);
    } else {
        renderExisting();
    }

    window.DCMSFileInput = {
        version: 1,
        refresh: function (target) {
            var root = resolve(target);
            if (root) { render(root); }
        },
        clear: function (target) {
            var root = resolve(target);
            var input = inputOf(root);
            if (!input) { return; }
            writeFiles(input, []);
            render(root);
            emit(input);
        }
    };
})();
